import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Grid, Paper, Box,
  IconButton, CircularProgress, Divider, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Refresh,
  CurrencyRupee, Receipt, TrendingUp, LocalOffer, AccountBalance,
} from '@mui/icons-material';
import TopBar from '../../../components/TopBar';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
} from 'recharts';
import { revenueAnalyticsService, salarySlipService, expenseService } from '../../../services/api';

// â”€â”€ helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const toInputDate = (d) => d.toISOString().split('T')[0];

const fmtCurrency = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtShort = (n) => {
  const v = Number(n || 0);
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000)   return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

const PAYMENT_COLORS = { Cash: '#4caf50', Card: '#2196f3', UPI: '#ff9800' };
const TYPE_COLORS    = { OPD: '#1565c0', IPD: '#9c27b0', Direct: '#607d8b' };
const PALETTE        = ['#1565c0','#2e7d32','#e65100','#6a1b9a','#c62828','#00695c','#f57f17','#0277bd'];

// â”€â”€ quick-range presets â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const QUICK_RANGES = [
  { label: 'Today',       key: 'today'  },
  { label: 'This Week',   key: 'week'   },
  { label: 'This Month',  key: 'month'  },
  { label: 'Last 30 Days',key: 'last30' },
];

const getQuickRange = (key) => {
  const today = new Date();
  const fmt   = (d) => toInputDate(d);
  switch (key) {
    case 'today': return { from: fmt(today), to: fmt(today) };
    case 'week': {
      const d = new Date(today);
      const day = today.getDay();
      d.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
      return { from: fmt(d), to: fmt(today) };
    }
    case 'month':
      return { from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt(today) };
    case 'last30':
    default: {
      const d = new Date(today);
      d.setDate(today.getDate() - 29);
      return { from: fmt(d), to: fmt(today) };
    }
  }
};

// â”€â”€ sub-components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const SummaryCard = ({ title, value, color, icon }) => (
  <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, borderLeft: `4px solid ${color}`, height: '100%' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color }}>{value}</Typography>
      </Box>
      <Box sx={{ color, opacity: 0.6 }}>{icon}</Box>
    </Box>
  </Paper>
);

const ChartCard = ({ title, height = 300, children }) => (
  <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{title}</Typography>
    <Box sx={{ height }}>{children}</Box>
  </Paper>
);

const EmptyState = () => (
  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Typography variant="body2" color="text.secondary">No data for selected period</Typography>
  </Box>
);

// Custom tooltip shown when hovering chart bars
const CurrencyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid #e0e0e0', minWidth: 160 }}>
      {label && (
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
          {label}
        </Typography>
      )}
      {payload.map((p, i) => (
        <Typography key={i} variant="caption" sx={{ display: 'block', color: p.fill || p.color }}>
          {p.name}: {p.name === 'Bills' ? p.value : fmtCurrency(p.value)}
        </Typography>
      ))}
    </Paper>
  );
};

// Percentage label rendered inside pie slices
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const R = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * R);
  const y = cy + r * Math.sin(-midAngle * R);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
      fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// â”€â”€ Main component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const RevenueDashboard = () => {
  const navigate = useNavigate();

  const init  = getQuickRange('last30');
  const [dateFrom,     setDateFrom]     = useState(init.from);
  const [dateTo,       setDateTo]       = useState(init.to);
  const [activeQuick,  setActiveQuick]  = useState('last30');
  const [refreshKey,   setRefreshKey]   = useState(0);
  const [loading,      setLoading]      = useState(false);

  const [summary,      setSummary]      = useState(null);
  const [trend,        setTrend]        = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [byType,       setByType]       = useState([]);
  const [byService,    setByService]    = useState([]);
  const [byDoctor,     setByDoctor]     = useState([]);
  const [recentBills,  setRecentBills]  = useState([]);
  const [opCost,       setOpCost]       = useState({ payroll: 0, expenses: 0 });

  // Fetch all analytics whenever dates or refresh key change
  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [sumRes, trendRes, pmRes, typeRes, svcRes, staffRes, recentRes] = await Promise.all([
          revenueAnalyticsService.getSummary(dateFrom, dateTo),
          revenueAnalyticsService.getTrend(dateFrom, dateTo),
          revenueAnalyticsService.getPaymentModes(dateFrom, dateTo),
          revenueAnalyticsService.getByType(dateFrom, dateTo),
          revenueAnalyticsService.getByService(dateFrom, dateTo),
          revenueAnalyticsService.getByDoctor(dateFrom, dateTo),
          revenueAnalyticsService.getRecentBills(dateFrom, dateTo),
        ]);
        if (cancelled) return;
        setSummary(sumRes.data);
        setTrend(trendRes.data);
        setPaymentModes(pmRes.data);
        setByType(typeRes.data);
        setByService(svcRes.data);
        setByDoctor(staffRes.data);
        setRecentBills(recentRes.data);

        // Operating cost: use current month/year derived from dateFrom
        const refDate = new Date(dateFrom);
        const curMonth = refDate.getMonth() + 1;
        const curYear  = refDate.getFullYear();
        try {
          const [slipRes, expRes] = await Promise.all([
            salarySlipService.getAll({ month: curMonth, year: curYear }),
            expenseService.getSummary({ month: curMonth, year: curYear }),
          ]);
          if (!cancelled) {
            const payroll  = (slipRes.data || []).reduce((s, x) => s + (x.netSalary || 0), 0);
            const expenses = expRes.data?.grandTotal || 0;
            setOpCost({ payroll, expenses });
          }
        } catch { /* non-critical — don't block revenue dashboard */ }
      } catch (err) {
        console.error('Analytics load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, refreshKey]);

  const handleQuick = (key) => {
    const r = getQuickRange(key);
    setDateFrom(r.from);
    setDateTo(r.to);
    setActiveQuick(key);
  };

  const handleDateInput = (field, val) => {
    if (field === 'from') setDateFrom(val);
    else setDateTo(val);
    setActiveQuick(null);
  };

  // Shape data for Recharts
  const trendData = trend.map((t) => ({
    label: new Date(t.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    Revenue: Number(t.revenue),
  }));

  const pmData      = paymentModes.map((p) => ({ name: p.mode,    value: Number(p.revenue), count: p.count }));
  const typeData    = byType.map((t)       => ({ name: t.type,    value: Number(t.revenue), count: t.count }));
  const serviceData = byService.map((s)    => ({ service: s.service, Revenue: Number(s.revenue) }));
  const staffData   = byDoctor.map((s)     => ({ name: s.name,   Revenue: Number(s.revenue), Bills: s.billCount }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* â”€â”€ AppBar â”€â”€ */}
      <TopBar
        title="Revenue Dashboard"
        onBack={() => navigate(-1)}
        actions={
          <>
            {loading && <CircularProgress size={22} color="inherit" sx={{ mr: 2 }} />}
            <IconButton color="inherit" title="Refresh" onClick={() => setRefreshKey((k) => k + 1)}>
              <Refresh />
            </IconButton>
          </>
        }
      />

      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* â”€â”€ Date Range Filter â”€â”€ */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Period:</Typography>
            {QUICK_RANGES.map((qr) => (
              <Chip
                key={qr.key}
                label={qr.label}
                size="small"
                onClick={() => handleQuick(qr.key)}
                color={activeQuick === qr.key ? 'primary' : 'default'}
                variant={activeQuick === qr.key ? 'filled' : 'outlined'}
              />
            ))}
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">From:</Typography>
              <input
                type="date"
                value={dateFrom}
                max={dateTo}
                onChange={(e) => handleDateInput('from', e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #bbb', fontSize: 13 }}
              />
              <Typography variant="body2">To:</Typography>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                max={toInputDate(new Date())}
                onChange={(e) => handleDateInput('to', e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #bbb', fontSize: 13 }}
              />
            </Box>
          </Box>
        </Paper>

        {/* â”€â”€ 1. Summary Cards â”€â”€ */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { title: 'Total Revenue',    value: fmtCurrency(summary?.totalRevenue),     color: '#2e7d32', icon: <CurrencyRupee sx={{ fontSize: 34 }} /> },
            { title: 'Total Bills',      value: summary?.totalBills ?? 0,               color: '#1565c0', icon: <Receipt      sx={{ fontSize: 34 }} /> },
            { title: 'Avg Bill Value',   value: fmtCurrency(summary?.averageBillValue), color: '#e65100', icon: <TrendingUp   sx={{ fontSize: 34 }} /> },
            { title: 'Total Discount',   value: fmtCurrency(summary?.totalDiscount),    color: '#c62828', icon: <LocalOffer   sx={{ fontSize: 34 }} /> },
            { title: 'Tax Collected',    value: fmtCurrency(summary?.totalTax),         color: '#6a1b9a', icon: <AccountBalance sx={{ fontSize: 34 }} /> },
          ].map((card) => (
            <Grid item xs={12} sm={6} lg={2.4} key={card.title}>
              <SummaryCard {...card} />
            </Grid>
          ))}
        </Grid>

        {/* â”€â”€ Operating Cost Summary card â”€â”€ */}
        {(opCost.payroll > 0 || opCost.expenses > 0) && (() => {
          const totalCost = opCost.payroll + opCost.expenses;
          const revenue   = summary?.totalRevenue || 0;
          const surplus   = revenue - totalCost;
          return (
            <Paper
              elevation={2}
              sx={{
                mb: 3, p: 2, borderRadius: 2,
                background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
                color: '#fff',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5, opacity: 0.85, letterSpacing: 1 }}>
                OPERATING COST SNAPSHOT (current month)
              </Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'Net Payroll',     value: fmtShort(opCost.payroll),  color: '#90caf9' },
                  { label: 'Other Expenses',  value: fmtShort(opCost.expenses), color: '#ef9a9a' },
                  { label: 'Total Cost',      value: fmtShort(totalCost),       color: '#fff176' },
                  {
                    label: surplus >= 0 ? 'Surplus' : 'Deficit',
                    value: fmtShort(Math.abs(surplus)),
                    color: surplus >= 0 ? '#a5d6a7' : '#ff8a80',
                  },
                ].map(c => (
                  <Grid item xs={6} sm={3} key={c.label}>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>{c.label}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: c.color }}>{c.value}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          );
        })()}

        {/* â”€â”€ 2. Revenue Trend â”€â”€ */}
        <Box sx={{ mb: 3 }}>
          <ChartCard title="Daily Revenue Trend" height={300}>
            {trendData.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CurrencyTooltip />} />
                  <Bar dataKey="Revenue" fill="#1565c0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        {/* â”€â”€ 3 & 5. Payment Mode + OPD/IPD side by side â”€â”€ */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <ChartCard title="Payment Mode Breakdown" height={290}>
              {pmData.length === 0 ? <EmptyState /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pmData} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" outerRadius={100}
                      labelLine={false} label={renderPieLabel}>
                      {pmData.map((e, i) => (
                        <Cell key={i} fill={PAYMENT_COLORS[e.name] || PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, name) => [fmtCurrency(v), name]} />
                    <Legend formatter={(name) => {
                      const d = pmData.find((p) => p.name === name);
                      return `${name} (${d?.count ?? 0} bills)`;
                    }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard title="OPD vs IPD Revenue" height={290}>
              {typeData.length === 0 ? <EmptyState /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={typeData} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" outerRadius={100}
                      labelLine={false} label={renderPieLabel}>
                      {typeData.map((e, i) => (
                        <Cell key={i} fill={TYPE_COLORS[e.name] || PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, name) => [fmtCurrency(v), name]} />
                    <Legend formatter={(name) => {
                      const d = typeData.find((t) => t.name === name);
                      return `${name} (${d?.count ?? 0} bills)`;
                    }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
        </Grid>

        {/* â”€â”€ 4. Revenue by Service (horizontal bar) â”€â”€ */}
        <Box sx={{ mb: 3 }}>
          <ChartCard title="Top Services by Revenue" height={Math.max(220, serviceData.length * 48 + 60)}>
            {serviceData.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={serviceData} layout="vertical"
                  margin={{ top: 5, right: 50, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tickFormatter={fmtShort} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="service" width={170} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CurrencyTooltip />} />
                  <Bar dataKey="Revenue" fill="#00695c" radius={[0, 4, 4, 0]}
                    label={{ position: 'right', formatter: fmtShort, fontSize: 11 }} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        {/* â”€â”€ 6. Revenue by Doctor â”€â”€ */}
        <Box sx={{ mb: 3 }}>
          <ChartCard title="Revenue by Doctor" height={Math.max(220, staffData.length * 55 + 60)}>
            {staffData.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={staffData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CurrencyTooltip />} />
                  <Legend />
                  <Bar dataKey="Revenue" fill="#c62828" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Bills"   fill="#ff8a65" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        {/* â”€â”€ 7. Recent Bills Table â”€â”€ */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Bills</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Receipt #</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date &amp; Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Patient</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Payment Mode</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Net Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Billed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentBills.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No bills in the selected period
                    </TableCell>
                  </TableRow>
                ) : (
                  recentBills.map((b) => (
                    <TableRow key={b.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>#{b.id}</TableCell>
                      <TableCell>
                        {new Date(b.billDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        {' '}
                        {new Date(b.billDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell>{b.patientName}</TableCell>
                      <TableCell>
                        <Chip
                          label={b.paymentMode}
                          size="small"
                          sx={{
                            bgcolor: (PAYMENT_COLORS[b.paymentMode] || '#888') + '22',
                            color:    PAYMENT_COLORS[b.paymentMode] || '#555',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {fmtCurrency(b.netAmount)}
                      </TableCell>
                      <TableCell>{b.billedBy}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

      </Container>
    </div>
  );
};

export default RevenueDashboard;
